import { Button, Modal } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import type { PanelTable } from "@/modules/pos/components/OrderPanel";

/**
 * "The party moved to the window table."
 *
 * Only FREE tables are offered: transferring onto an occupied table is a
 * server-side conflict by design (one open invoice per table), and a button
 * that can only fail teaches staff to distrust the till. The occupied race —
 * someone seats that table between render and tap — still comes back as a
 * typed conflict the toast can say out loud.
 */
export function TransferTableDialog({
  tables,
  currentTableId,
  isSubmitting,
  onCancel,
  onPick,
}: {
  tables: PanelTable[];
  currentTableId: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onPick: (tableId: string) => void | Promise<void>;
}) {
  const t = useTranslate();
  const freeTables = tables.filter(
    (table) => table.invoiceId === null && table.tableId !== currentTableId,
  );

  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      title={t((m) => m.pos.transferTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="transfer-dialog"
      /* NO DISMISSAL GUARD HERE, deliberately: nothing on this dialog is
         typed. Every control is a table, and a tap on one commits — so a
         swipe-down discards no work, and a prompt would tax the ordinary way
         out of a dialog somebody opened by mistake.

         The footer carries a real Cancel because the X in the header was the
         only way out, and a dialog whose body is a wall of equal-weight
         commit buttons needs one control that is certainly not one of them. */
      footerActions={
        <Button
          variant="outline"
          size="touch"
          onClick={onCancel}
          data-testid="transfer-cancel"
        >
          {t((m) => m.common.cancel)}
        </Button>
      }
    >
      {freeTables.length === 0 ? (
        <p className="py-4 text-center text-sm font-semibold text-[color:var(--text-muted)]">
          {t((m) => m.pos.transferNoFreeTables)}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2 py-1">
          {freeTables.map((table) => (
            <Button
              key={table.tableId}
              variant="outline"
              size="touch"
              className="font-extrabold"
              disabled={isSubmitting}
              data-testid={`transfer-to-${table.tableId}`}
              onClick={() => void onPick(table.tableId)}
            >
              {table.name}
            </Button>
          ))}
        </div>
      )}
    </Modal>
  );
}
